import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { 
  Building2, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  ArrowRight,
  Lightbulb,
  MessageSquare
} from 'lucide-react';
import { projectId } from '../utils/supabase/info';
import type { JSMSpaceAssessmentResponse } from '../types';

interface JSMAssessmentCardProps {
  accessToken: string;
  companyId: string;
  companyName: string;
  currentUser: any;
  onStartAssessment: () => void;
  onViewAssessment: () => void;
}

export function JSMAssessmentCard({
  accessToken,
  companyId,
  companyName,
  currentUser,
  onStartAssessment,
  onViewAssessment
}: JSMAssessmentCardProps) {
  const [assessment, setAssessment] = useState<JSMSpaceAssessmentResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const isConsultant = currentUser?.role === 'consultant' || currentUser?.role === 'systemAdmin';

  useEffect(() => {
    loadAssessment();
  }, [companyId]);

  const loadAssessment = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/companies/${companyId}/jsm-assessment`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAssessment(data.assessment);
      }
    } catch (error) {
      console.error('Error loading JSM assessment:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return null;
  }

  // Don't show if assessment is completed and approved
  if (assessment?.status === 'approved') {
    return null;
  }

  // CONSULTANT VIEW - Assessment submitted, needs review
  if (isConsultant && assessment?.status === 'submitted') {
    return (
      <Card className="border-orange-200 bg-orange-50/50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Building2 className="h-6 w-6 text-orange-600" />
              <div>
                <CardTitle className="text-orange-900">JSM Space Assessment Ready for Review</CardTitle>
                <CardDescription className="text-orange-700">
                  {companyName} has completed their assessment
                </CardDescription>
              </div>
            </div>
            <Badge className="bg-orange-600">Action Needed</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert className="border-orange-200 bg-white">
            <MessageSquare className="h-4 w-4 text-orange-600" />
            <AlertDescription>
              <strong>Recommendation:</strong> {assessment.recommendedSpaceCount} JSM Space{assessment.recommendedSpaceCount > 1 ? 's' : ''}
              {assessment.recommendedStructure && assessment.recommendedStructure.length > 0 && (
                <div className="mt-2 text-sm">
                  {assessment.recommendedStructure.map((space, idx) => (
                    <div key={idx} className="text-muted-foreground">
                      • {space.spaceName}: {space.teams.join(', ')}
                    </div>
                  ))}
                </div>
              )}
            </AlertDescription>
          </Alert>

          <div className="flex gap-2">
            <Button onClick={onViewAssessment} className="bg-orange-600 hover:bg-orange-700">
              Review Assessment
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // CLIENT VIEW - Assessment submitted, awaiting review
  if (!isConsultant && assessment?.status === 'submitted') {
    return (
      <Card className="border-blue-200 bg-blue-50/50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Clock className="h-6 w-6 text-blue-600" />
              <div>
                <CardTitle className="text-blue-900">JSM Space Assessment Submitted</CardTitle>
                <CardDescription className="text-blue-700">
                  Awaiting consultant review
                </CardDescription>
              </div>
            </div>
            <Badge className="bg-blue-600">In Review</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert className="border-blue-200 bg-white">
            <CheckCircle className="h-4 w-4 text-blue-600" />
            <AlertDescription>
              <strong>Your recommendation:</strong> {assessment.recommendedSpaceCount} JSM Space{assessment.recommendedSpaceCount > 1 ? 's' : ''}
              <p className="mt-2 text-sm text-muted-foreground">
                Your consultant will review your assessment and provide feedback soon. 
                You'll receive a notification when it's approved.
              </p>
            </AlertDescription>
          </Alert>

          <Button variant="outline" onClick={onViewAssessment}>
            View My Assessment
          </Button>
        </CardContent>
      </Card>
    );
  }

  // CLIENT VIEW - Assessment in draft or not started
  if (!isConsultant && (!assessment || assessment.status === 'draft')) {
    return (
      <Card className="border-primary/20 bg-primary/5">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Building2 className="h-6 w-6 text-primary" />
              <div>
                <CardTitle>Start Your JSM Space Design Assessment</CardTitle>
                <CardDescription>
                  Determine the optimal JSM architecture for your organization
                </CardDescription>
              </div>
            </div>
            {assessment?.status === 'draft' && (
              <Badge variant="secondary">In Progress</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <p className="text-sm">
              Before configuring your Jira Service Management implementation, determine
              <strong> how many JSM Spaces (Projects) you need</strong>. This critical decision affects:
            </p>
            
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Customer experience</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Team workflows</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Privacy controls</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Reporting structure</span>
              </div>
            </div>
          </div>

          <Alert>
            <Lightbulb className="h-4 w-4" />
            <AlertDescription>
              <strong>Interactive Assessment</strong> - Answer 8 guided questions to receive a 
              personalized recommendation. Takes ~10-15 minutes.
            </AlertDescription>
          </Alert>

          <div className="flex gap-2">
            <Button onClick={onStartAssessment} size="lg">
              {assessment?.status === 'draft' ? 'Continue Assessment' : 'Start Assessment'}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            {assessment?.status === 'draft' && (
              <Button variant="outline" onClick={onViewAssessment}>
                View Progress
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // CONSULTANT VIEW - No assessment started
  if (isConsultant && !assessment) {
    return (
      <Card className="border-muted">
        <CardHeader>
          <div className="flex items-center gap-3">
            <Building2 className="h-6 w-6 text-muted-foreground" />
            <div>
              <CardTitle className="text-muted-foreground">JSM Space Assessment</CardTitle>
              <CardDescription>
                Client hasn't started the assessment yet
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Consider encouraging {companyName} to complete the JSM Space Design Assessment 
            to determine the optimal architecture before configuration begins.
          </p>
          <Button variant="outline" onClick={onViewAssessment} size="sm">
            View Assessment Tool
          </Button>
        </CardContent>
      </Card>
    );
  }

  return null;
}